import { queryOptions } from '@tanstack/react-query';

import { api } from '@lib/api/client';
import type { EnhancedStoreCustomer } from '@marxbiotech/shared-types/medusa';
import type { AccountLayoutData as AccountLayoutDataBase } from '@marxbiotech/shared-types/bff';

// =============================================================================
// Types
// =============================================================================

/**
 * Client-side account layout data.
 *
 * Narrows the BFF wire type (`AccountLayoutData`) to use `EnhancedStoreCustomer`
 * for typed metadata access (e.g. `StoreCustomerMetadata`, `StoreCustomerAddressMetadata`).
 */
export interface AccountLayoutData extends Omit<AccountLayoutDataBase, 'customer'> {
  customer: EnhancedStoreCustomer | null;
}

// =============================================================================
// Query Functions
// =============================================================================

const fetchAccountLayoutData = async (): Promise<AccountLayoutData> => {
  const response = await api.get<AccountLayoutData>('/layout/account');
  return response.data;
};

// =============================================================================
// Query Keys
// =============================================================================

export const accountKeys = {
  all: ['account'] as const,
  layout: () => [...accountKeys.all, 'layout'] as const,
};

// =============================================================================
// Query Options
// =============================================================================

export const accountLayoutQueryOptions = () =>
  queryOptions({
    queryKey: accountKeys.layout(),
    queryFn: fetchAccountLayoutData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
